import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Divider, Form, Icon } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import AuthService from '~/api/services/auth';
import { FormFields } from '~/components/FormFields';
import notify from '~/components/Notify';
import { MAX_LENGTH_PASSWORD, MINIMUM_LENGTH_PASSWORD, passwordRegex, passwordRegexWithoutSpecChar } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import { RouteTypes } from '~/routes/constants';
import LayoutAuth from './LayoutAuth'
import { El } from './style';
import { handleErrors } from '~/utils/helpers';

const ForceChangePassForm: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setForcePassword, fetchSystemData } = useAuth()
  const { logoTitle, firstColor, companyName } = useContext(ThemeContext)

  const [isBusy, setIsBusy] = useState(false)
  const [visibleCurrent, setVisibleCurrent] = useState(false)
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleRepeatPassword, setVisibleRepeatPassword] = useState(false);
  const [updatePasswordSettings, setUpdatePasswordSettings] = useState(null)

  const matchPasswordErrorMessage = t('Passwords do not match');
  const { handleSubmit, control, getValues } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    fetchUpdatePasswordData()
  }, [])

  async function fetchUpdatePasswordData() {
    setIsBusy(true);
    try {
      const response = await AuthService.updatePasswordData()
      setUpdatePasswordSettings(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  async function onSubmitPassword(data) {
    setIsBusy(true);
    try {
      await AuthService.updatePassword(data)
      setForcePassword(false)
      fetchSystemData(true)
      notify(t('Password set'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderForm() {
    return <Form
      style={El.loginForm}
      onSubmit={handleSubmit(onSubmitPassword)}
      autoComplete={'off'}
    >
      <El.FixedPrice style={{ backgroundImage: `url(${logoTitle})` }} />
      <El.FormLogo style={{ color: firstColor }}>{companyName}</El.FormLogo>
      <Divider section />
      <FormFields.Input
        control={control}
        style={El.loginFormField}
        label={t('Current password')}
        name='currentPassword'
        size='large'
        type={visibleCurrent ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visibleCurrent ? 'eye slash' : 'eye'}
            onClick={() => setVisibleCurrent(!visibleCurrent)}
          />
        }
        rules={{
          required: {
            value: true,
            message: t('Please enter password')
          },
          pattern: {
            value: passwordRegexWithoutSpecChar,
            message: t(
              'The password must contain at least one uppercase, lowercase letter, and at least one symbol'
            )
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 8 characters long')
          },
          maxLength: {
            value: MAX_LENGTH_PASSWORD,
            message: t('Max character is 32')
          }
        }}
        placeholder={t('Enter current password')}
      />
      <FormFields.Input
        control={control}
        style={El.loginFormField}
        label={t('Enter new password')}
        name='password'
        size='large'
        type={visiblePassword ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visiblePassword ? 'eye slash' : 'eye'}
            onClick={() => setVisiblePassword(!visiblePassword)}
          />
        }
        rules={{
          required: {
            value: true,
            message: t('Please enter new password')
          },
          pattern: {
            value: updatePasswordSettings?.passwordSecurity ? passwordRegex : passwordRegexWithoutSpecChar,
            message: t(
              'The password must contain at least one uppercase, lowercase letter, and at least one symbol'
            )
          },
          minLength: {
            value: MINIMUM_LENGTH_PASSWORD,
            message: t('Password must be at least 8 characters long')
          },
          maxLength: {
            value: MAX_LENGTH_PASSWORD,
            message: t('Max character is 32')
          }
        }}
        placeholder={t('Enter new password')}
      />
      <FormFields.Input
        style={El.loginFormField}
        label={t('Confirm new password')}
        name='passwordConfirmation'
        type={visibleRepeatPassword ? 'text' : 'password'}
        icon={
          <Icon
            link
            name={visibleRepeatPassword ? 'eye slash' : 'eye'}
            onClick={() => setVisibleRepeatPassword(!visibleRepeatPassword)}
          />
        }
        size='large'
        rules={{
          required: {
            value: true,
            message: t('Please enter password confirmation')
          },
          validate: (value) => getValues('password') === value || matchPasswordErrorMessage
        }}
        placeholder={t('Enter new password')}
        control={control}
      />
      <Form.Group style={{ justifyContent: 'center' }}>
        <Form.Field style={El.loginFormSubmitWrapper}>
          <Button
            style={El.loginFormSubmitButton}
            primary
            content={t('Confirm')}
          />
        </Form.Field>
        <Form.Field style={El.loginFormSubmitWrapper}>
          <Button
            style={El.loginFormSubmitButton}
            primary
            onClick={() => {
              setForcePassword(false)
              localStorage.removeItem('token')
              navigate(RouteTypes.LOGIN)
            }}
            content={t('Return to login')}
          />
        </Form.Field>
      </Form.Group>
      <Divider />
    </Form>
  }

  return <LayoutAuth isLoading={isBusy}>
    {renderForm()}
  </LayoutAuth>
}

export default ForceChangePassForm